import { useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  CustomFieldEntityType,
  CustomFieldType,
  type CustomFieldDefinition,
} from 'src/api/generated';
import {
  useCreateCustomFieldDefinition,
  useUpdateCustomFieldDefinition,
  useCustomFieldDefinition,
} from 'src/modules/shared/hooks/api/custom-fields/definitions';
import { Text } from 'src/modules/shared/components/atoms/text';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import TextInput from 'src/modules/shared/components/atoms/inputs/text-input';
import TextArea from 'src/modules/shared/components/atoms/inputs/text-area';
import Select from 'src/modules/shared/components/atoms/inputs/select';
import LabelSelect from 'src/modules/shared/components/atoms/inputs/label-select';
import { FormField } from 'src/modules/shared/components/molecules/form-field';
import { Panel } from 'src/modules/shared/components/molecules/panel';
import Loading from 'src/modules/shared/components/atoms/loading';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import {
  getCustomFieldTypeOptions,
  validateFieldKey,
} from 'src/modules/settings/utils/custom-fields.util';
import {
  useGetDefinitionFunnels,
  useReplaceDefinitionFunnels,
} from 'src/modules/shared/hooks/api/crm/funnels/custom-field-definition-funnels';
import { useFunnels } from 'src/modules/shared/hooks/api/crm/funnels/funnels';
import Checkbox from 'src/modules/shared/components/atoms/checkbox';
import LabelSwitch from 'src/modules/shared/components/atoms/inputs/label-switch';
import { Alert } from 'src/modules/shared/components/atoms/alert';
import { checkDirty } from 'src/modules/shared/utils/array.util';
import {
  buildCustomFieldDefinitionIncludeTimePayload,
  resolveDateCustomFieldIncludeTime,
} from 'src/modules/shared/utils/custom-field-definition.util';
import { useAlert } from 'src/contexts/alert-context';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';

const customFieldDefinitionSchema = z.object({
  fieldKey: z.string().min(1, 'El identificador del campo es requerido'),
  fieldLabel: z.string().optional(),
  fieldType: z.nativeEnum(CustomFieldType),
  description: z.string().optional(),
  includeTime: z.boolean().optional(),
});

type CustomFieldDefinitionFormData = z.infer<
  typeof customFieldDefinitionSchema
>;

interface CustomFieldDefinitionFormProps {
  entityType: CustomFieldEntityType;
  definitionId?: number | null;
  onSave: () => void;
  onCancel: () => void;
  onEditListOptions: (definition: CustomFieldDefinition) => void;
  onDirtyChange?: (dirty: boolean) => void;
  showFunnelMapping?: boolean;
  initialFunnelId?: number;
  allowEntityChange?: boolean;
}

export const CustomFieldDefinitionForm: React.FC<
  CustomFieldDefinitionFormProps
> = ({
  entityType,
  definitionId,
  onSave,
  onCancel,
  onDirtyChange,
  showFunnelMapping = true,
  initialFunnelId,
  allowEntityChange = false,
}) => {
  const isEditing = !!definitionId;
  const [newDefinitionId, setNewDefinitionId] = useState<number | undefined>(
    undefined
  );

  const [localEntityType, setLocalEntityType] =
    useState<CustomFieldEntityType>(entityType);

  const { funnels, loading: loadingFunnels } = useFunnels();
  const [selectedFunnelIds, setSelectedFunnelIds] = useState<string[]>(
    initialFunnelId ? [String(initialFunnelId)] : []
  );
  const [initialFunnelIds, setInitialFunnelIds] = useState<string[]>(
    initialFunnelId ? [String(initialFunnelId)] : []
  );

  const getNextSelectedFunnelIds = (
    previousIds: string[],
    isChecked: boolean,
    funnelIdValue: string
  ): string[] => {
    if (isChecked && !previousIds.includes(funnelIdValue)) {
      return [...previousIds, funnelIdValue];
    }
    if (!isChecked) {
      return previousIds.filter((v) => v !== funnelIdValue);
    }
    return previousIds;
  };

  const effectiveShowFunnelMapping =
    showFunnelMapping &&
    (!allowEntityChange || localEntityType !== CustomFieldEntityType.PRODUCT);

  const { data: definitionFunnels, loading: loadingDefinitionFunnels } =
    useGetDefinitionFunnels(
      isEditing ? definitionId : undefined,
      isEditing && effectiveShowFunnelMapping
    );

  useEffect(() => {
    if (isEditing && definitionFunnels) {
      const loaded = definitionFunnels.funnelIds.map((id) => String(id));
      setSelectedFunnelIds(loaded);
      setInitialFunnelIds(loaded);
    }
  }, [isEditing, definitionFunnels]);

  const {
    definition,
    loading: loadingDefinition,
    error: definitionError,
  } = useCustomFieldDefinition(definitionId ?? 0, isEditing);

  const { createDefinition, loading: creating } =
    useCreateCustomFieldDefinition();

  const { updateDefinition, loading: updating } =
    useUpdateCustomFieldDefinition();

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    reset,
    watch,
    setError,
    setValue,
  } = useForm<CustomFieldDefinitionFormData>({
    resolver: zodResolver(customFieldDefinitionSchema),
    mode: 'onChange',
    defaultValues: {
      fieldKey: '',
      fieldLabel: '',
      fieldType: undefined,
      description: '',
      includeTime: false,
    },
  });

  const fieldType = watch('fieldType');
  const includeTime = watch('includeTime');
  const rawMappingsDirty = useMemo(
    () => checkDirty(selectedFunnelIds, initialFunnelIds),
    [selectedFunnelIds, initialFunnelIds]
  );
  const mappingsDirty = effectiveShowFunnelMapping ? rawMappingsDirty : false;
  const typeOptions = useMemo(() => getCustomFieldTypeOptions(), []);

  useEffect(() => {
    if (definition && isEditing) {
      // Use reset to set baseline defaults and avoid marking the form dirty
      reset({
        fieldKey: definition.fieldKey,
        fieldLabel: definition.fieldLabel ?? '',
        fieldType: definition.fieldType,
        description: definition.description ?? '',
        includeTime: resolveDateCustomFieldIncludeTime(
          definition.fieldType,
          definition.includeTime
        ),
      });
    }
  }, [definition, isEditing, reset]);

  const { replaceFunnels, loading: replacingFunnels } =
    useReplaceDefinitionFunnels(isEditing ? definitionId : newDefinitionId);
  const isBusy = creating || updating || replacingFunnels;

  const { showAlert } = useAlert();
  const { t } = useI18n();

  // Report dirty state to parent (form changes or funnel mapping changes)
  useEffect(() => {
    onDirtyChange?.(Boolean(isDirty || mappingsDirty));

    return () => {
      onDirtyChange?.(false);
    };
  }, [isDirty, mappingsDirty, onDirtyChange]);

  useEffect(() => {
    if (!isEditing) {
      setLocalEntityType(entityType);
    }
  }, [entityType, isEditing]);

  const handleCancel = () => {
    // Reset form and mappings to initial values before closing
    if (isEditing && definition) {
      reset({
        fieldKey: definition.fieldKey,
        fieldLabel: definition.fieldLabel ?? '',
        fieldType: definition.fieldType,
        description: definition.description ?? '',
        includeTime: resolveDateCustomFieldIncludeTime(
          definition.fieldType,
          definition.includeTime
        ),
      });
    } else {
      reset({
        fieldKey: '',
        fieldLabel: '',
        fieldType: undefined,
        description: '',
        includeTime: false,
      });
    }
    setSelectedFunnelIds(initialFunnelIds);
    onDirtyChange?.(false);
    onCancel();
  };

  const onSubmit = async (data: CustomFieldDefinitionFormData) => {
    // Validate field key
    const fieldKeyError = validateFieldKey(data.fieldKey);
    if (fieldKeyError) {
      setError('fieldKey', { message: fieldKeyError });
      return;
    }

    try {
      if (isEditing && definitionId && definition) {
        await updateDefinition({
          id: definitionId,
          data: {
            fieldLabel: data.fieldLabel,
            description: data.description,
            includeTime: buildCustomFieldDefinitionIncludeTimePayload(
              definition.fieldType,
              data.includeTime
            ),
          },
        });

        if (effectiveShowFunnelMapping) {
          try {
            await replaceFunnels(selectedFunnelIds.map((v) => Number(v)));
          } catch (e) {
            console.error('Failed to update definition funnel mappings:', e);
          }
        }

        // Reset dirty state before navigation
        reset({
          fieldKey: data.fieldKey,
          fieldLabel: data.fieldLabel ?? '',
          fieldType: data.fieldType,
          description: data.description ?? '',
          includeTime: Boolean(data.includeTime),
        });
        onDirtyChange?.(false);
        onSave();
      } else {
        const created = await createDefinition({
          entityType: localEntityType,
          fieldKey: data.fieldKey,
          fieldLabel: data.fieldLabel,
          fieldType: data.fieldType,
          description: data.description,
          includeTime: buildCustomFieldDefinitionIncludeTimePayload(
            data.fieldType,
            data.includeTime
          ),
        });

        if (effectiveShowFunnelMapping) {
          try {
            setNewDefinitionId(created.id);
            await replaceFunnels({
              funnelIds: selectedFunnelIds.map((v) => Number(v)),
              definitionIdOverride: created.id,
            });
          } catch (e) {
            console.error('Failed to update definition funnel mappings:', e);
          }
        }

        // Clear form & dirty state before navigating away
        reset({
          fieldKey: '',
          fieldLabel: '',
          fieldType: undefined,
          description: '',
          includeTime: false,
        });
        onDirtyChange?.(false);
        onSave();
      }
    } catch (error) {
      console.error('Error saving custom field definition:', error);
      // TODO: check when implementing i18n
      showAlert(
        'No se pudo guardar el campo personalizado. Inténtalo de nuevo.',
        'error'
      );
    }
  };

  if (loadingDefinition) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loading size="lg" />
      </div>
    );
  }

  if (definitionError) {
    return (
      <ErrorState
        // TODO: check when implementing i18n
        title="Error cargando el campo personalizado"
        // TODO: check when implementing i18n
        description="No se pudo cargar el campo personalizado. Por favor, inténtalo de nuevo."
        onAction={onCancel}
        // TODO: check when implementing i18n
        actionContent="Volver"
      />
    );
  }

  return (
    <Panel variant="secondary" appearance="outline">
      <div className="p-4">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              // TODO: check when implementing i18n
              label="Identificador"
              error={errors.fieldKey?.message}
              required
              // TODO: check when implementing i18n
              helpText="Identificador único del campo (solo letras, números y guiones bajos)"
            >
              <TextInput
                {...register('fieldKey')}
                // TODO: check when implementing i18n
                placeholder="ej: fuente_lead"
                disabled={isEditing}
              />
            </FormField>

            <FormField
              // TODO: check when implementing i18n
              label="Nombre"
              error={errors.fieldLabel?.message}
              // TODO: check when implementing i18n
              helpText="Nombre que se mostrará en la interfaz a los usuarios"
            >
              <TextInput
                {...register('fieldLabel')}
                // TODO: check when implementing i18n
                placeholder="ej: Fuente del Lead"
              />
            </FormField>

            {/* TODO: check when implementing i18n */}
            <FormField label="Tipo" error={errors.fieldType?.message} required>
              <Controller
                name="fieldType"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Select
                    value={value}
                    onChange={(e) => onChange(e.target.value || undefined)}
                    options={[
                      // TODO: check when implementing i18n
                      { value: '', content: 'Selecciona el tipo de campo' },
                      ...typeOptions,
                    ]}
                    disabled={isEditing}
                  />
                )}
              />
            </FormField>
          </div>

          <FormField
            // TODO: check when implementing i18n
            label="Descripción"
            error={errors.description?.message}
            // TODO: check when implementing i18n
            helpText="Descripción opcional del propósito del campo"
          >
            <TextArea
              {...register('description')}
              // TODO: check when implementing i18n
              placeholder="Describe para qué se utiliza este campo..."
              rows={3}
            />
          </FormField>

          {fieldType === CustomFieldType.DATE && (
            <div className="space-y-3">
              <div className="flex justify-end">
                <LabelSwitch
                  checked={Boolean(includeTime)}
                  onChange={(checked) =>
                    setValue('includeTime', checked, { shouldDirty: true })
                  }
                  label={t('settings.customFields.includeTime.label')}
                  size="sm"
                />
              </div>

              <Alert
                variant="secondary"
                appearance="soft"
                size="sm"
                message={t('settings.customFields.includeTime.warning')}
              />
            </div>
          )}

          {fieldType === CustomFieldType.LIST && !isEditing && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <Text size="small" className="text-blue-700">
                {/* TODO: check when implementing i18n */}
                Luego de crear el campo de tipo Lista, podrás configurar las
                opciones posibles.
              </Text>
            </div>
          )}

          {!isEditing && allowEntityChange && (
            <div className="mt-6 pt-4 border-t">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <LabelSelect
                  label="Entidad"
                  labelPosition="top"
                  options={[
                    {
                      value: CustomFieldEntityType.FUNNEL_CONVERSATION,
                      content: 'Conversación',
                    },
                    {
                      value: CustomFieldEntityType.CONTACT,
                      content: 'Contacto',
                    },
                    {
                      value: CustomFieldEntityType.PRODUCT,
                      content: 'Producto',
                    },
                  ]}
                  selectProps={{
                    value: localEntityType,
                    onChange: (e) =>
                      setLocalEntityType(
                        e.target.value as CustomFieldEntityType
                      ),
                  }}
                />
              </div>
            </div>
          )}

          {effectiveShowFunnelMapping && (
            <div className="mt-6 pt-4 border-t space-y-4">
              <div>
                <Text size="small" className="text-gray-700 font-medium">
                  {/* TODO: check when implementing i18n */}
                  Embudos donde estará disponible
                </Text>
                {loadingFunnels || loadingDefinitionFunnels ? (
                  <div className="mt-3 py-4 flex items-center">
                    <Loading size="sm" />
                  </div>
                ) : (
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {funnels.map((f) => {
                      const value = String(f.id);
                      const checked = selectedFunnelIds.includes(value);
                      return (
                        <Checkbox
                          key={f.id}
                          checked={checked}
                          onChange={(next: boolean) => {
                            setSelectedFunnelIds((prev) =>
                              getNextSelectedFunnelIds(prev, next, value)
                            );
                          }}
                          label={f.name}
                          size="sm"
                          className="py-1"
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="neutral"
              appearance="ghost"
              onClick={handleCancel}
              disabled={isBusy}
            >
              {/* TODO: check when implementing i18n */}
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={isBusy}
              disabled={
                isBusy || (isEditing ? !mappingsDirty && !isDirty : !isValid)
              }
            >
              {/* TODO: check when implementing i18n */}
              {isEditing ? 'Actualizar' : 'Crear'}
            </Button>
          </div>
        </form>
      </div>
    </Panel>
  );
};
